import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { EditorTool, ScreenPos } from '../types/scene'

export const useSelectionStore = defineStore('selection', () => {
  const selectedId = ref<string | null>(null)
  const selectedIds = ref<string[]>([])   // 多选
  /** 多选时属性面板「主编辑」对象，须属于 selectedIds */
  const primaryDetailId = ref<string | null>(null)
  const bubblePos = ref<ScreenPos | null>(null)
  const currentFloorId = ref<string>('f1')
  const focusRequestId = ref<string | null>(null)   // ObjectListPanel 请求 SceneCanvas focus 到此对象
  /** 右键菜单等请求 ObjectListPanel 进入行内重命名 */
  const renameRequestId = ref<string | null>(null)
  /** 对象观察面板是否固定（固定后切换选中对象时面板不随对象移动） */
  const bubbleMenuPinned = ref(true)
  /** 固定时的面板位置，用于任意对象的面板共用同一位置 */
  const bubbleMenuFixedPos = ref<{ left: number; top: number } | null>(null)
  /** 从组件托盘拖拽中的组件类型，用于 Ghost 预览 */
  const draggingComponentType = ref<string | null>(null)
  /** TopBar 测量工具：开启后画布点击放置测点，不选中对象 */
  const measureToolActive = ref(false)
  /** 路径 / 折线道路 / 折线围墙（与测量互斥） */
  const editorTool = ref<EditorTool>('none')
  /** 折线道路笔画预设 id（roadPresets） */
  const roadStrokePresetId = ref<string>('lane2')
  const roadStrokeColor = ref<string>('#555555')
  const roadStrokeShowCenterLine = ref<boolean>(true)
  const roadStrokeHasCurb = ref<boolean>(false)
  const roadStrokeCurbColor = ref<string>('#8b8b8b')
  /** 折线管线笔画预设 id（pipePresets） */
  const pipeStrokePresetId = ref<string>('medium')
  /** 折线围墙笔画参数 */
  const wallStrokeThickness = ref<number>(0.4)
  const wallStrokeHeight = ref<number>(3)
  const pathToolActive = computed(() => editorTool.value === 'path')
  /** 当前选中的路径 id */
  const selectedPathId = ref<string | null>(null)
  /** 仅允许编辑某一楼层（如二层）；null 表示不限制 */
  const exclusiveEditFloorId = ref<string | null>(null)

  function setDraggingComponentType(type: string | null) {
    draggingComponentType.value = type
  }

  function setBubbleMenuPinned(pinned: boolean) {
    bubbleMenuPinned.value = pinned
  }

  function setBubbleMenuFixedPos(left: number, top: number) {
    bubbleMenuFixedPos.value = { left, top }
  }

  function setBubblePos(pos: ScreenPos | null) {
    bubblePos.value = pos
  }

  function syncPrimaryDetailId() {
    const ids = selectedIds.value
    if (ids.length === 0) {
      primaryDetailId.value = null
      return
    }
    if (ids.length === 1) {
      primaryDetailId.value = ids[0]!
      return
    }
    const p = primaryDetailId.value
    if (p && ids.includes(p)) return
    primaryDetailId.value = ids[0]!
  }

  function setPrimaryDetailId(id: string) {
    if (!selectedIds.value.includes(id)) return
    primaryDetailId.value = id
  }

  function select(id: string, screenPos: ScreenPos) {
    selectedPathId.value = null
    selectedId.value = id
    selectedIds.value = [id]
    bubblePos.value = screenPos
    syncPrimaryDetailId()
  }

  function multiSelect(id: string, screenPos: ScreenPos) {
    selectedPathId.value = null
    if (selectedIds.value.includes(id)) {
      selectedIds.value = selectedIds.value.filter(i => i !== id)
    } else {
      selectedIds.value = [...selectedIds.value, id]
    }
    selectedId.value = selectedIds.value[0] ?? null
    bubblePos.value = screenPos
    syncPrimaryDetailId()
  }

  function deselect() {
    selectedId.value = null
    selectedIds.value = []
    primaryDetailId.value = null
    bubblePos.value = null
  }

  /** 新建工程：恢复编辑器交互态（楼层、工具、选中等） */
  function resetForFreshProject() {
    deselect()
    currentFloorId.value = 'f1'
    measureToolActive.value = false
    editorTool.value = 'none'
    selectedPathId.value = null
    exclusiveEditFloorId.value = null
    draggingComponentType.value = null
  }

  function setFloor(floorId: string) {
    currentFloorId.value = floorId
  }

  function setMeasureToolActive(v: boolean) {
    measureToolActive.value = v
    if (v) editorTool.value = 'none'
  }

  function toggleMeasureTool() {
    if (measureToolActive.value) {
      measureToolActive.value = false
    } else {
      measureToolActive.value = true
      editorTool.value = 'none'
    }
  }

  function setEditorTool(t: EditorTool) {
    if (t !== 'none') measureToolActive.value = false
    editorTool.value = t
  }

  function setPathToolActive(v: boolean) {
    if (v) {
      editorTool.value = 'path'
      measureToolActive.value = false
    } else if (editorTool.value === 'path') {
      editorTool.value = 'none'
    }
  }

  function togglePathTool() {
    if (editorTool.value === 'path') editorTool.value = 'none'
    else {
      editorTool.value = 'path'
      measureToolActive.value = false
    }
  }

  function toggleRoadStrokeTool() {
    if (editorTool.value === 'roadStroke') editorTool.value = 'none'
    else {
      editorTool.value = 'roadStroke'
      measureToolActive.value = false
    }
  }

  function toggleWallStrokeTool() {
    if (editorTool.value === 'wallStroke') editorTool.value = 'none'
    else {
      editorTool.value = 'wallStroke'
      measureToolActive.value = false
    }
  }

  function setRoadStrokePresetId(id: string) {
    roadStrokePresetId.value = id
  }

  function setRoadStrokeColor(v: string) {
    roadStrokeColor.value = typeof v === 'string' && v ? v : '#555555'
  }

  function setRoadStrokeShowCenterLine(v: boolean) {
    roadStrokeShowCenterLine.value = !!v
  }

  function setRoadStrokeHasCurb(v: boolean) {
    roadStrokeHasCurb.value = !!v
  }

  function setRoadStrokeCurbColor(v: string) {
    roadStrokeCurbColor.value = typeof v === 'string' && v ? v : '#8b8b8b'
  }

  function togglePipeStrokeTool() {
    if (editorTool.value === 'pipeStroke') editorTool.value = 'none'
    else {
      editorTool.value = 'pipeStroke'
      measureToolActive.value = false
    }
  }

  function setPipeStrokePresetId(id: string) {
    pipeStrokePresetId.value = id
  }

  function setWallStrokeThickness(v: number) {
    const n = Number(v)
    if (!Number.isFinite(n)) return
    wallStrokeThickness.value = Math.min(5, Math.max(0.1, n))
  }

  function setWallStrokeHeight(v: number) {
    const n = Number(v)
    if (!Number.isFinite(n)) return
    wallStrokeHeight.value = Math.min(20, Math.max(0.5, n))
  }

  function selectPath(pathId: string | null) {
    selectedPathId.value = pathId
    if (pathId) {
      selectedId.value = null
      selectedIds.value = []
      primaryDetailId.value = null
      bubblePos.value = null
    }
  }

  function setExclusiveEditFloor(floorId: string | null) {
    exclusiveEditFloorId.value = floorId
  }

  /** 请求 SceneCanvas 把相机 focus 到指定对象（ObjectListPanel 调用） */
  function requestFocus(id: string) {
    selectedPathId.value = null
    selectedId.value = id
    selectedIds.value = [id]
    focusRequestId.value = id
    syncPrimaryDetailId()
  }

  /** 请求对象列表对指定对象进入重命名（与双击名称一致） */
  function requestRename(id: string) {
    selectedPathId.value = null
    selectedId.value = id
    selectedIds.value = [id]
    renameRequestId.value = id
    syncPrimaryDetailId()
  }

  /** 对象列表 Shift 范围多选（不移动相机、不显示气泡） */
  function selectListRange(ids: string[]) {
    selectedPathId.value = null
    selectedIds.value = ids.length ? [...ids] : []
    selectedId.value = ids[0] ?? null
    bubblePos.value = null
    syncPrimaryDetailId()
  }

  /** 对象列表 Ctrl 多选切换（不显示气泡） */
  function listMultiToggle(id: string) {
    selectedPathId.value = null
    if (selectedIds.value.includes(id)) {
      selectedIds.value = selectedIds.value.filter(i => i !== id)
    } else {
      selectedIds.value = [...selectedIds.value, id]
    }
    selectedId.value = selectedIds.value[0] ?? null
    bubblePos.value = null
    syncPrimaryDetailId()
  }

  return {
    selectedId,
    selectedIds,
    primaryDetailId,
    bubblePos,
    currentFloorId,
    focusRequestId,
    renameRequestId,
    bubbleMenuPinned,
    bubbleMenuFixedPos,
    draggingComponentType,
    measureToolActive,
    editorTool,
    roadStrokePresetId,
    roadStrokeColor,
    roadStrokeShowCenterLine,
    roadStrokeHasCurb,
    roadStrokeCurbColor,
    pipeStrokePresetId,
    wallStrokeThickness,
    wallStrokeHeight,
    pathToolActive,
    selectedPathId,
    exclusiveEditFloorId,
    setMeasureToolActive,
    toggleMeasureTool,
    setEditorTool,
    setPathToolActive,
    togglePathTool,
    toggleRoadStrokeTool,
    toggleWallStrokeTool,
    togglePipeStrokeTool,
    setRoadStrokePresetId,
    setRoadStrokeColor,
    setRoadStrokeShowCenterLine,
    setRoadStrokeHasCurb,
    setRoadStrokeCurbColor,
    setPipeStrokePresetId,
    setWallStrokeThickness,
    setWallStrokeHeight,
    selectPath,
    setExclusiveEditFloor,
    setBubbleMenuPinned,
    setBubbleMenuFixedPos,
    setBubblePos,
    setDraggingComponentType,
    select,
    multiSelect,
    deselect,
    resetForFreshProject,
    setFloor,
    requestFocus,
    requestRename,
    selectListRange,
    listMultiToggle,
    setPrimaryDetailId
  }
})
